import { useEffect, useRef, useState } from "react";
import { X, Camera } from "lucide-react";
import jsQR from "jsqr";

export function qrTokenFromText(text) {
    const m = String(text || "").match(/\/naradi\/qr\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : (/^[A-Za-z0-9_-]{8,}$/.test(String(text || "").trim()) ? String(text).trim() : null);
}

export function QrScanner({ onDecode, onClose, hint }) {
    const videoRef = useRef(); const canvasRef = useRef(); const lastRef = useRef({ text: "", at: 0 });
    const [error, setError] = useState("");
    const [last, setLast] = useState("");
    useEffect(() => {
        let stream, raf, stop = false;
        const detector = "BarcodeDetector" in window ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;
        const emit = (text) => {
            const now = Date.now();
            if (!text || (lastRef.current.text === text && now - lastRef.current.at < 2500)) return;
            lastRef.current = { text, at: now }; setLast(text);
            if (navigator.vibrate) navigator.vibrate(60);
            onDecode(text);
        };
        const tick = async () => {
            if (stop) return;
            const v = videoRef.current;
            if (v && v.readyState >= 2) {
                try {
                    if (detector) {
                        const codes = await detector.detect(v);
                        if (codes[0]) emit(codes[0].rawValue);
                    } else {
                        const c = canvasRef.current; const w = v.videoWidth, h = v.videoHeight;
                        if (w && h) {
                            c.width = w; c.height = h;
                            const g = c.getContext("2d", { willReadFrequently: true }); g.drawImage(v, 0, 0, w, h);
                            const code = jsQR(g.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: "dontInvert" });
                            if (code?.data) emit(code.data);
                        }
                    }
                } catch { /* ignore frame errors */ }
            }
            raf = setTimeout(tick, detector ? 150 : 250);
        };
        navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
            .then((s) => { stream = s; if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); } tick(); })
            .catch((e) => setError(e?.name === "NotAllowedError" ? "Přístup ke kameře je zakázaný – povolte ho v nastavení prohlížeče." : "Kameru se nepodařilo spustit."));
        return () => { stop = true; clearTimeout(raf); stream?.getTracks().forEach((t) => t.stop()); };
    }, [onDecode]);
    return (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="qr-scanner">
            <div className="flex items-center justify-between px-4 py-3 text-white">
                <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider"><Camera className="w-4 h-4" /> Skenovat QR štítek</div>
                <button onClick={onClose} data-testid="qr-scanner-close" className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-sm hover:bg-white/10"><X className="w-6 h-6" /></button>
            </div>
            <div className="relative flex-1 overflow-hidden">
                <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
                <canvas ref={canvasRef} className="hidden" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="w-64 h-64 border-4 border-orange-500/90 rounded-sm shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" /></div>
                {error && <div className="absolute inset-x-4 top-4 bg-red-600 text-white text-sm p-3 rounded-sm" data-testid="qr-scanner-error">{error}</div>}
            </div>
            <div className="px-4 py-4 text-white text-sm text-center min-h-[64px]">{last ? <span className="text-emerald-300">Načteno · namiřte na další štítek</span> : (hint || "Namiřte kameru na QR štítek nářadí")}</div>
        </div>
    );
}
